// Talent dashboard: the SELECTION the page sends, and the display arithmetic it
// does on what comes back. There is no aggregation here any more.
//
// WHAT LEFT, AND WHY: this file used to hold `aggregateSellers`, `stripTotals`,
// `filterAggregates`, `sortAggregates` and `dimensionOptions` — the whole fold,
// run in the browser over every `(seller, day)` row in the warehouse. That is what
// made the payload grow with the table until it crossed Cloud Run's 32 MiB
// response cap and took the dashboard down for four days (issue #1211). The fold
// now runs in SQL next to the data and the page receives one answered page.
//
// WHAT STAYED: dividing. The server ships counts, denominators and (sum, count)
// latency pairs exactly as it always did, and the client divides ONCE, here, for
// display. Averaging per-day rates would weight a 1-thread day like a 50-thread day
// (Simpson's paradox); that reasoning is unchanged and so is every number.
import { type DateRange } from "@recruiter/shared";
import type {
  TalentQuerySelection,
  TalentSellerAggregate,
  TalentStripTotals,
} from "@recruiter/shared/types";

/** A rate as its raw parts, so a caller can render "42% (21/50)" and a reader can
 *  audit it. `value` is null when the denominator is zero: 0/0 is UNKNOWN, and
 *  rendering it as 0% would read as a real, terrible score. */
export interface Ratio {
  value: number | null;
  numerator: number;
  denominator: number;
}

export function ratio(numerator: number, denominator: number): Ratio {
  return { value: denominator > 0 ? numerator / denominator : null, numerator, denominator };
}

/** A mean from an additive (sum, count) pair. Null when nothing was measured —
 *  again UNKNOWN, not zero: "0 minutes to reply" is a lie, not a fast seller. */
export function mean(sum: number | null | undefined, count: number): number | null {
  return count > 0 && sum != null ? sum / count : null;
}

export type { DateRange };

/** The value a null dimension carries on the wire, and the option the filter
 *  offers for it. Must equal the backend's `UNKNOWN_VALUE` — a drift here silently
 *  stops the "Unknown" bucket from selecting anything. */
export const UNKNOWN = "unknown";

/** The sort keys the table offers.
 *
 *  A CLOSED set that the SERVER also holds (`TalentSortKey` in
 *  `recruiter/talent/models.py`, mapped to expressions in `query.py`), because a
 *  sort key becomes an ORDER BY — the one clause a query parameter cannot reach.
 *  `test_every_sort_key_the_console_offers_is_orderable_here` reads this very union
 *  and fails if the two sets drift, which is what stops a column being sortable in
 *  the UI and inert (or 500ing) on the server. */
export type SortKey =
  | "sellerId"
  | "approached"
  | "responseRate"
  | "avgResponseMinutes"
  | "avgTimeToOfferMinutes"
  | "questions"
  | "willingness"
  | "chosen"
  | "chosenRate"
  | "country"
  | "sellerLevel"
  | "searchTier"
  | "searchTierPoints"
  | "subCategoryId";

/** Every dimension filter is MULTI-select: a list of accepted values.
 *
 *  Empty list = no constraint. Within one dimension the values are OR'd ("Portugal
 *  or Spain"); across dimensions they are AND'd ("Portugal, and Top tier"). That is
 *  the only reading that makes a multi-select useful — AND-ing values inside one
 *  dimension would always return nothing, since a seller has one country. The
 *  server applies exactly this reading. */
export interface Filters {
  q: string;
  country: readonly string[];
  sellerLevel: readonly string[];
  searchTier: readonly string[];
  subCategory: readonly string[];
  managed: readonly string[]; // "yes" | "no" | UNKNOWN
}

export const EMPTY_FILTERS: Filters = {
  q: "",
  country: [],
  sellerLevel: [],
  searchTier: [],
  subCategory: [],
  managed: [],
};

/** True when nothing is narrowing the list, so the page can say whether the
 *  headline numbers are the whole population or a slice of it. */
export function hasActiveFilters(f: Filters): boolean {
  return (
    f.q.trim() !== "" ||
    f.country.length > 0 ||
    f.sellerLevel.length > 0 ||
    f.searchTier.length > 0 ||
    f.subCategory.length > 0 ||
    f.managed.length > 0
  );
}

/** The page size. One "Show more" step, and the server's own ceiling is 500. */
export const PAGE_SIZE = 200;

/** Build the SELECTION for what the reader is currently looking at.
 *
 *  This is the whole request: range, filters, order, page. It is also the cache
 *  KEY the server derives, so two readers looking at the same thing share one
 *  answer — which is why it is built in one place from the page's state rather
 *  than assembled at each call site. */
export function selectionFor(
  range: DateRange,
  filters: Filters,
  sort: SortKey,
  direction: "asc" | "desc",
  limit: number = PAGE_SIZE,
  offset = 0,
): TalentQuerySelection {
  return {
    dateFrom: range.from ?? null,
    dateTo: range.to ?? null,
    q: filters.q,
    sellerId: "",
    country: [...filters.country],
    sellerLevel: [...filters.sellerLevel],
    searchTier: [...filters.searchTier],
    subCategory: [...filters.subCategory],
    managed: [...filters.managed],
    sort,
    direction,
    limit,
    offset,
  };
}

/** The selection for ONE named talent's lifetime, for the explorer's statistics.
 *
 *  `sellerId` rather than `q`: `q` is a substring match, so it would also match
 *  `avatar_pro` when the page is about `ava`, and the panel would describe the
 *  wrong person while looking completely normal. */
export function selectionForSeller(sellerId: string): TalentQuerySelection {
  return {
    dateFrom: null,
    dateTo: null,
    q: "",
    sellerId,
    country: [],
    sellerLevel: [],
    searchTier: [],
    subCategory: [],
    managed: [],
    sort: "approached",
    direction: "desc",
    limit: 1,
    offset: 0,
  };
}

/** The rates a seller row displays, divided once from the counts the server sent.
 *
 *  DENOMINATORS: response rate divides by approaches (owner, 2026-07-29);
 *  willingness divides by the approaches the talent WROTE BACK in (owner,
 *  2026-08-03). They are deliberately different questions — "did we reach them"
 *  versus "once reached, did they offer" — and neither matches the all-time
 *  `mira_kpi` views, which condition on a GENUINE (timestamped) reply. That is a
 *  choice, not a bug; the column titles say which is which. */
export interface SellerRates {
  responseRate: Ratio;
  willingness: Ratio;
  chosenRate: Ratio;
  avgResponseMinutes: number | null;
  avgTimeToOfferMinutes: number | null;
}

export function sellerRates(row: TalentSellerAggregate): SellerRates {
  return {
    responseRate: ratio(row.replied, row.approached),
    willingness: ratio(row.offers, row.wroteBack),
    chosenRate: ratio(row.chosen, row.approached),
    avgResponseMinutes: mean(row.replyMinutesSum, row.replyMinutesCount),
    avgTimeToOfferMinutes: mean(row.offerMinutesSum, row.offerMinutesCount),
  };
}

/** The strip's four headline figures, divided once from the server's totals.
 *
 *  The totals cover the WHOLE selection, not the page — that is the difference
 *  server-side aggregation buys, and it is why narrowing to Portugal still narrows
 *  these numbers even though the table only holds 200 rows.
 *
 *  The two medians come from the server already computed: they are the middle
 *  SELLER's own average latency, deliberately a DIFFERENT statistic from the pooled
 *  mean beside them (one prolific straggler drags a mean and cannot move a median),
 *  and they are not additive, so they cannot be re-derived from a page. */
export interface StripFigures {
  sellersApproached: number;
  projectsApproached: number;
  responseRate: Ratio;
  willingness: Ratio;
  avgResponseMinutes: number | null;
  avgTimeToOfferMinutes: number | null;
  medianResponseMinutes: number | null;
  medianTimeToOfferMinutes: number | null;
  questionsPerProject: number | null;
  questions: number;
}

export function stripFigures(t: TalentStripTotals): StripFigures {
  return {
    sellersApproached: t.sellersApproached,
    projectsApproached: t.projectsApproached,
    responseRate: ratio(t.replied, t.projectsApproached),
    willingness: ratio(t.offers, t.wroteBack),
    avgResponseMinutes: mean(t.replyMinutesSum, t.replyMinutesCount),
    avgTimeToOfferMinutes: mean(t.offerMinutesSum, t.offerMinutesCount),
    medianResponseMinutes: t.medianResponseMinutes ?? null,
    medianTimeToOfferMinutes: t.medianTimeToOfferMinutes ?? null,
    questionsPerProject: t.projectsApproached > 0 ? t.questions / t.projectsApproached : null,
    questions: t.questions,
  };
}

/** The filter value a row presents on the is-managed axis. A null is UNKNOWN and
 *  says so; it is never folded in with "no". */
export function managedKey(isManaged: boolean | null | undefined): string {
  return isManaged == null ? UNKNOWN : isManaged ? "yes" : "no";
}
